import React from 'react';

import { useIntl } from 'react-intl';

import { GET_PRICING_PLAN_RESOURCE } from 'src/features/pricingPlan/api/useGetPricingPlan';
import { LIST_PRICING_PLANS_RESOURCE } from 'src/features/pricingPlan/api/useListPricingPlans';
import { usePartialUpdatePricingPlan } from 'src/features/pricingPlan/api/usePartialUpdatePricingPlan';
import { useUpdatePricingPlan } from 'src/features/pricingPlan/api/useUpdatePricingPlan';
import PricingPlanFormModal from 'src/features/pricingPlan/components/form/PricingPlanFormModal';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { PricingPlan } from 'src/features/pricingPlan/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import { DataTableNewEntryProps } from 'src/registry/components/table';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

interface UpdatePricingPlanFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedPricingPlan?: PricingPlan;
    children?: React.ReactNode;
}

function UpdatePricingPlanFormModal<TData>({
    className,
    semantics,
    children,
    selectedPricingPlan,
}: UpdatePricingPlanFormModalProps<TData>) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const onSuccess = (success: PricingPlan) => {
        void queryClient.invalidateQueries({ queryKey: [LIST_PRICING_PLANS_RESOURCE] });
        void queryClient.invalidateQueries({ queryKey: [GET_PRICING_PLAN_RESOURCE] });
        toast({
            title: formatMessage(pricingPlanMessages.updatePricingPlanSuccess),
            description: formatMessage(pricingPlanMessages.updatePricingPlanSuccessDescription, {
                entry: success.translations.find((translation) => translation.language === locale)?.name,
            }),
        });
    };

    const { mutate, isPending } = useUpdatePricingPlan({ onSuccess });
    // Published plans only allow editing name/description, so that edit goes through a PATCH call
    const { mutate: partialMutate, isPending: isPartialPending } = usePartialUpdatePricingPlan({ onSuccess });

    return (
        <PricingPlanFormModal
            selectedPricingPlan={selectedPricingPlan}
            className={className}
            mutate={(data, onSuccess, onError) =>
                mutate({ data, planId: selectedPricingPlan?.id ?? '', parkingId }, { onSuccess, onError })
            }
            mutatePartial={(data, onSuccess, onError) =>
                partialMutate({ data, planId: selectedPricingPlan?.id ?? '', parkingId }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending || isPartialPending}
            subtitle={formatMessage(pricingPlanMessages.editPricingPlanSubtitle, {
                entry: selectedPricingPlan?.translations.find((translation) => translation.language === locale)?.name,
            })}
        >
            {children}
        </PricingPlanFormModal>
    );
}

export default UpdatePricingPlanFormModal;
